import { Component, Inject, Input, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CustomerCard, Ticket } from '@ridango/components';
import { AppStartupService, AuthService, BaseComponent, TicketProductType } from '@ridango/core';
import { OffsetContext } from '@ridango/core/lib/enums/ridango-date-time-pipe-context.enum';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import Env from '@ridango/core/env';
import { BehaviorSubject, of } from 'rxjs';
import { ActionSheetController, ActionSheetOptions, PopoverController } from '@ionic/angular';
import { TicketContextualMenuComponent } from '../ticket-contextual-menu/ticket-contextual-menu.component';
import {
    fixedValidityRangeOptionsFromAppStartup,
    ridangoDateTimeValidityOptions,
    resolveFixedValidityRange,
    shouldUseDateTimeForQrTicket,
    useUnifiedTicketValidityDisplay
} from '../../../../services/ticket-validity-display.helper';
import { TicketActionsService, TicketData } from '../../../../services/ticket-actions/ticket-actions.service';
import { TicketBackdropHighlightService } from '../../../../services/ticket-backdrop-highlight.service';

@Component({
    selector: 'rwidget-card-tickets-list',
    templateUrl: './card-tickets-list.component.html'
})
export class CardTicketsListComponent extends BaseComponent implements OnInit, OnDestroy {
    @Input() card: CustomerCard;

    tickets: Ticket[] = [];
    locale: string;
    timezone: string;
    dateTimeFormat: string;
    primaryDateTimeFormat: string;
    TicketProductType = TicketProductType;
    locale$ = new BehaviorSubject<string>(this.translate.currentLang);
    /**
     * Determines if ticket should display full datetime format instead of date-only format.
     * Delegates to shared helper function.
     */
    shouldUseDateTimeForQrTicket = shouldUseDateTimeForQrTicket;

    constructor(
        @Inject('ENV') public env: Env,
        public translate: TranslateService,
        public appStartupService: AppStartupService,
        private httpClient: HttpClient,
        private actionSheetCtrl: ActionSheetController,
        private popoverCtrl: PopoverController,
        private ticketActionsService: TicketActionsService,
        private authService: AuthService,
        private backdropHighlight: TicketBackdropHighlightService
    ) {
        super();
    }

    ngOnInit(): void {
        this.fetchTickets();
        this.locale = this.translate.currentLang;
        this.dateTimeFormat = this.env.PERIOD_TICKET_DATE_FORMAT;
        this.primaryDateTimeFormat = this.env.PRIMARY_DATE_TIME_FORMAT || this.dateTimeFormat;
        this.timezone = this.appStartupService.getTimezone();

        this.translate.onLangChange.pipe(takeUntil(this.componentDestroyed$)).subscribe((event) => {
            this.locale$.next(event.lang);
        });
    }

    fetchTickets(): void {
        const url = `${this.env.CORE_API_URL}/customer/v3/region/${this.env.REGION_ID}/card/${this.card?.rawCardNumber}/tickets`;
        const params = this.env.CHANNEL ? { channel: this.env.CHANNEL } : {};

        this.httpClient
            .get<Ticket[]>(url, { params })
            .pipe(
                map((response) =>
                    [...response]
                        .map((ticket) => ({
                            ...ticket,
                            cardNumber: this.card?.cardNumber
                        }))
                        .sort((a, b) => {
                            if (a.validated === b.validated) return 0;
                            return a.validated ? -1 : 1;
                        })
                ),
                catchError((error) => {
                    console.error('Failed to fetch tickets:', error);
                    return of([]);
                }),
                takeUntil(this.componentDestroyed$)
            )
            .subscribe((tickets) => {
                this.tickets = tickets;
            });
    }

    async openTicketPopover(ticket: Ticket, event: Event): Promise<void> {
        const ctx = {
            ticket: ticket as TicketData,
            env: this.env,
            isHistory: false,
            isAuth: this.authService.getLoginStatus(),
            locale: this.translate.currentLang
        };
        const buttons = this.ticketActionsService.createActionsList(ctx, 'Card ticket action sheet', 'onSheet');
        const options: ActionSheetOptions = {
            cssClass: 'ticket-actions',
            mode: 'ios',
            buttons
        };
        const actionSheet = await this.actionSheetCtrl.create(options);

        // adds role=button to action sheet buttons for WCAG
        actionSheet.present().then(() => {
            document.querySelectorAll('.action-sheet-button').forEach((el) => {
                el.setAttribute('role', 'button');
            });
        });

        this.highlightTicket(event);

        await actionSheet.present();
        await actionSheet.onDidDismiss().then(() => {
            this.backdropHighlight.reset();
        });
    }

    async openTicketDesktopPopover(ticket: Ticket, event: Event): Promise<void> {
        const ctx = {
            ticket: ticket as TicketData,
            env: this.env,
            isHistory: false,
            isAuth: this.authService.getLoginStatus(),
            locale: this.translate.currentLang
        };
        const actions = this.ticketActionsService.createActionsList(ctx, 'Card ticket popover', 'onSheet');
        const popover = await this.popoverCtrl.create({
            component: TicketContextualMenuComponent,
            componentProps: { actions, mode: 'default' },
            mode: 'ios',
            event
        });
        await popover.present();
        const liEl = (event.target as HTMLElement).closest('li') as HTMLElement | null;
        if (liEl) this.backdropHighlight.highlight(liEl);
        await popover.onDidDismiss().then(() => {
            this.backdropHighlight.reset();
        });
    }

    private highlightTicket(event: Event): void {
        const liEl = (event.target as HTMLElement).closest('li') as HTMLElement | null;
        if (!liEl) return;
        this.backdropHighlight.highlight(liEl);
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }

    get useUnifiedTicketValidityDisplay(): boolean {
        return useUnifiedTicketValidityDisplay(this.env);
    }

    ridangoDateTimeOptions(ticket: Ticket) {
        return ridangoDateTimeValidityOptions(this.env, ticket, OffsetContext.CARD_TICKETS_LIST);
    }

    hasFixedValidityRange(ticket: Ticket): boolean {
        return Boolean(
            resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))
        );
    }

    ticketValidityStart(ticket: Ticket): Date | string | undefined {
        return (
            resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))?.start ||
            ticket.starting
        );
    }

    ticketValidityEnd(ticket: Ticket): Date | string | undefined {
        return (
            resolveFixedValidityRange(ticket, fixedValidityRangeOptionsFromAppStartup(this.appStartupService))?.end ||
            ticket.ending
        );
    }
}
